import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Grid, MenuItem, FormControl, InputLabel, Select, Tooltip, Card,
  CardContent, Alert, CircularProgress, TablePagination, InputAdornment, Divider,
  useMediaQuery, useTheme, LinearProgress, Snackbar,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Visibility as ViewIcon, Apartment as ApartmentIcon, HomeWork as HomeWorkIcon,
  CheckCircle as CheckIcon, VpnKey as KeyIcon, Inventory2 as ItemsIcon,
} from '@mui/icons-material';
import { propertyService, dropdownService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const PROPERTY_TYPES = ['Office Building', 'Guest House', 'Male Guest House', 'Female Guest House', 'Warehouse', 'Studio', 'Residence', 'Shop', 'Land', 'Other'];
const STATUSES = ['Active', 'Inactive', 'Under Maintenance'];
const STATUS_COLORS = { Active: 'success', Inactive: 'default', 'Under Maintenance': 'warning' };
const OWNERSHIP = ['Owned', 'Rented', 'Leased'];
const ITEM_CONDITIONS = ['New', 'Good', 'Needs Repair', 'Damaged'];

const emptyForm = {
  name: '', propertyType: 'Office Building', status: 'Active', address: '', city: '',
  capacity: '', rooms: '', purpose: '', occupancyNotes: '',
  ownership: 'Owned', ownerName: '', monthlyRent: '', deposit: '',
  agreementStartDate: '', agreementEndDate: '',
  caretakerName: '', caretakerPhone: '', remarks: '',
  items: [],
};

const PropertyManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can } = useAuth();
  // can() returns true / false / null(not-loaded). Show unless explicitly denied.
  const allow = (v) => v !== false;
  const canCreate = allow(can('property', 'create'));
  const canEdit = allow(can('property', 'edit'));
  const canDelete = allow(can('property', 'delete'));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  // Admin-editable dropdowns (Configure → Property Type / Status / Ownership /
  // Item Condition). Falls back to the built-in defaults if not configured.
  const [dd, setDd] = useState({
    propertyTypes: PROPERTY_TYPES, propertyStatuses: STATUSES,
    ownershipTypes: OWNERSHIP, itemConditions: ITEM_CONDITIONS,
  });

  const showToast = (msg, severity = 'success') => setToast({ open: true, msg, severity });

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, limit: rowsPerPage };
      if (search) params.search = search;
      if (filterType) params.propertyType = filterType;
      if (filterStatus) params.status = filterStatus;
      const res = await propertyService.getAll(params);
      setRows(res.data.data?.properties || []);
      setTotalCount(res.data.data?.total || 0);
    } catch (e) {
      showToast('Failed to load properties', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await propertyService.getStatistics();
      setStats(res.data.data);
    } catch (e) { /* non-fatal */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [page, rowsPerPage, filterType, filterStatus]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStats(); }, []);

  // Load admin-configured dropdowns; keep defaults on any failure/empty.
  useEffect(() => {
    dropdownService.getAll().then((res) => {
      const d = res.data?.data || {};
      setDd({
        propertyTypes: d.propertyTypes?.length ? d.propertyTypes : PROPERTY_TYPES,
        propertyStatuses: d.propertyStatuses?.length ? d.propertyStatuses : STATUSES,
        ownershipTypes: d.ownershipTypes?.length ? d.ownershipTypes : OWNERSHIP,
        itemConditions: d.itemConditions?.length ? d.itemConditions : ITEM_CONDITIONS,
      });
    }).catch(() => {});
  }, []);

  const handleSearch = () => { setPage(0); loadData(); };

  const openCreate = () => { setSelected(null); setForm(emptyForm); setFormError(''); setOpenForm(true); };
  const openEdit = (row) => {
    setSelected(row);
    setForm({
      ...emptyForm, ...row,
      capacity: row.capacity ?? '', rooms: row.rooms ?? '',
      monthlyRent: row.monthlyRent ?? '', deposit: row.deposit ?? '',
      agreementStartDate: row.agreementStartDate || '', agreementEndDate: row.agreementEndDate || '',
      items: Array.isArray(row.items) ? row.items : [],
    });
    setFormError('');
    setOpenForm(true);
  };

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Items list handlers
  const addItem = () => setForm((f) => ({ ...f, items: [...(f.items || []), { name: '', quantity: '', condition: 'Good', remarks: '' }] }));
  const updateItem = (i, key, val) => setForm((f) => {
    const items = (f.items || []).map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    return { ...f, items };
  });
  const removeItem = (i) => setForm((f) => ({ ...f, items: (f.items || []).filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.name || !form.name.trim()) { setFormError('Property name is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (selected) {
        await propertyService.update(selected.id, form);
        showToast('Property updated');
      } else {
        await propertyService.create(form);
        showToast('Property created');
      }
      setOpenForm(false);
      loadData();
      loadStats();
    } catch (e) {
      setFormError(e.response?.data?.message || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await propertyService.delete(selected.id);
      showToast('Property deleted');
      setOpenDelete(false);
      loadData();
      loadStats();
    } catch (e) {
      showToast(e.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const fmtMoney = (n) => (n != null && n !== '' ? '₹ ' + Number(n).toLocaleString('en-IN') : '—');
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
  const itemCount = (row) => (Array.isArray(row.items) ? row.items.length : 0);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Property Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage company properties — offices, guest houses, warehouses — and the items available in each.</Typography>
        </Box>
        {canCreate && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Property</Button>}
      </Box>

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Total Properties', value: stats.total, icon: <ApartmentIcon />, color: '#1565C0' },
            { label: 'Active', value: stats.active, icon: <CheckIcon />, color: '#2E7D32' },
            { label: 'Rented / Leased', value: stats.rented, icon: <KeyIcon />, color: '#ED6C02' },
            { label: 'Property Types', value: (stats.byType || []).length, icon: <HomeWorkIcon />, color: '#6A1B9A' },
          ].map((s) => (
            <Grid item xs={6} md={3} key={s.label}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                  <Box sx={{ color: s.color }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{s.value ?? 0}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Search name, type, city, caretaker…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Type</InputLabel>
          <Select label="Type" value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(0); }}>
            <MenuItem value="">All Types</MenuItem>
            {dd.propertyTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
            <MenuItem value="">All Statuses</MenuItem>
            {dd.propertyStatuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={handleSearch}>Search</Button>
      </Paper>

      {/* Table */}
      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                <TableCell>Property</TableCell>
                {!isMobile && <TableCell>Type</TableCell>}
                {!isMobile && <TableCell>City</TableCell>}
                {!isMobile && <TableCell>Ownership</TableCell>}
                {!isMobile && <TableCell align="center">Items</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No properties yet. Click "New Property" to add one.</TableCell></TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.id} hover onClick={() => { setSelected(row); setOpenView(true); }} sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.caretakerName || row.address || '—'}</Typography>
                  </TableCell>
                  {!isMobile && <TableCell>{row.propertyType || '—'}</TableCell>}
                  {!isMobile && <TableCell>{row.city || '—'}</TableCell>}
                  {!isMobile && <TableCell>{row.ownership || '—'}</TableCell>}
                  {!isMobile && <TableCell align="center">
                    <Chip size="small" icon={<ItemsIcon sx={{ fontSize: 14 }} />} label={itemCount(row)} variant="outlined" />
                  </TableCell>}
                  <TableCell><Chip size="small" label={row.status} color={STATUS_COLORS[row.status] || 'default'} /></TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View"><IconButton size="small" onClick={() => { setSelected(row); setOpenView(true); }}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                    {canEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                    {canDelete && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { setSelected(row); setOpenDelete(true); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={totalCount} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Create / Edit dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{selected ? 'Edit Property' : 'New Property'}</DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Core Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField required label="Property Name" fullWidth size="small" value={form.name} onChange={setField('name')} /></Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Property Type</InputLabel>
                <Select label="Property Type" value={form.propertyType} onChange={setField('propertyType')}>
                  {[...new Set([form.propertyType, ...dd.propertyTypes].filter(Boolean))].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}><TextField label="Address" fullWidth size="small" value={form.address} onChange={setField('address')} /></Grid>
            <Grid item xs={12} md={4}><TextField label="City" fullWidth size="small" value={form.city} onChange={setField('city')} /></Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={form.status} onChange={setField('status')}>
                  {dd.propertyStatuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Capacity &amp; Usage</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><TextField label="Capacity (people)" type="number" fullWidth size="small" value={form.capacity} onChange={setField('capacity')} /></Grid>
            <Grid item xs={6} md={3}><TextField label="Rooms" type="number" fullWidth size="small" value={form.rooms} onChange={setField('rooms')} /></Grid>
            <Grid item xs={12} md={6}><TextField label="Purpose / Usage" fullWidth size="small" value={form.purpose} onChange={setField('purpose')} /></Grid>
            <Grid item xs={12}><TextField label="Occupancy Notes" fullWidth size="small" multiline rows={2} value={form.occupancyNotes} onChange={setField('occupancyNotes')} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Ownership &amp; Finance</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Ownership</InputLabel>
                <Select label="Ownership" value={form.ownership} onChange={setField('ownership')}>
                  {dd.ownershipTypes.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}><TextField label="Owner / Landlord Name" fullWidth size="small" value={form.ownerName} onChange={setField('ownerName')} /></Grid>
            <Grid item xs={6} md={3}><TextField label="Monthly Rent (₹)" type="number" fullWidth size="small" value={form.monthlyRent} onChange={setField('monthlyRent')} /></Grid>
            <Grid item xs={6} md={3}><TextField label="Deposit (₹)" type="number" fullWidth size="small" value={form.deposit} onChange={setField('deposit')} /></Grid>
            <Grid item xs={6} md={3}><TextField label="Agreement Start" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.agreementStartDate || ''} onChange={setField('agreementStartDate')} /></Grid>
            <Grid item xs={6} md={3}><TextField label="Agreement End" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.agreementEndDate || ''} onChange={setField('agreementEndDate')} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Contacts &amp; Notes</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField label="Caretaker / Contact Name" fullWidth size="small" value={form.caretakerName} onChange={setField('caretakerName')} /></Grid>
            <Grid item xs={12} md={6}><TextField label="Caretaker Phone" fullWidth size="small" value={form.caretakerPhone} onChange={setField('caretakerPhone')} /></Grid>
            <Grid item xs={12}><TextField label="Remarks" fullWidth size="small" multiline rows={2} value={form.remarks} onChange={setField('remarks')} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="primary">Items / Assets in this Property</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addItem}>Add Item</Button>
          </Box>
          {(form.items || []).length === 0 && (
            <Typography variant="caption" color="text.secondary">No items added. Add fans, switch boxes, furniture, appliances, etc.</Typography>
          )}
          {(form.items || []).map((it, i) => (
            <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
              <Grid item xs={12} sm={4}><TextField label="Item" size="small" fullWidth value={it.name} onChange={(e) => updateItem(i, 'name', e.target.value)} placeholder="e.g. Ceiling Fan" /></Grid>
              <Grid item xs={4} sm={2}><TextField label="Qty" size="small" type="number" fullWidth value={it.quantity} onChange={(e) => updateItem(i, 'quantity', e.target.value)} /></Grid>
              <Grid item xs={8} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Condition</InputLabel>
                  <Select label="Condition" value={it.condition || 'Good'} onChange={(e) => updateItem(i, 'condition', e.target.value)}>
                    {dd.itemConditions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={10} sm={2}><TextField label="Remarks" size="small" fullWidth value={it.remarks} onChange={(e) => updateItem(i, 'remarks', e.target.value)} /></Grid>
              <Grid item xs={2} sm={1}><IconButton color="error" onClick={() => removeItem(i)}><DeleteIcon /></IconButton></Grid>
            </Grid>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving && <CircularProgress size={16} />}>
            {selected ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View dialog */}
      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selected?.name}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box>
              <Grid container spacing={1.5}>
                {[
                  ['Type', selected.propertyType],
                  ['Status', selected.status],
                  ['City', selected.city],
                  ['Address', selected.address],
                  ['Capacity', selected.capacity],
                  ['Rooms', selected.rooms],
                  ['Purpose', selected.purpose],
                  ['Ownership', selected.ownership],
                  ['Owner / Landlord', selected.ownerName],
                  ['Monthly Rent', fmtMoney(selected.monthlyRent)],
                  ['Deposit', fmtMoney(selected.deposit)],
                  ['Agreement', selected.agreementStartDate ? `${fmtDate(selected.agreementStartDate)} → ${fmtDate(selected.agreementEndDate)}` : '—'],
                  ['Caretaker', selected.caretakerName ? `${selected.caretakerName}${selected.caretakerPhone ? ' (' + selected.caretakerPhone + ')' : ''}` : '—'],
                ].map(([k, v]) => (
                  <Grid item xs={6} key={k}>
                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                    <Typography variant="body2" fontWeight={600}>{v || v === 0 ? v : '—'}</Typography>
                  </Grid>
                ))}
                {selected.occupancyNotes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Occupancy Notes</Typography>
                    <Typography variant="body2">{selected.occupancyNotes}</Typography>
                  </Grid>
                )}
                {selected.remarks && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Remarks</Typography>
                    <Typography variant="body2">{selected.remarks}</Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Items / Assets ({Array.isArray(selected.items) ? selected.items.length : 0})
              </Typography>
              {(!Array.isArray(selected.items) || selected.items.length === 0) && (
                <Typography variant="body2" color="text.secondary">No items recorded.</Typography>
              )}
              {Array.isArray(selected.items) && selected.items.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow sx={{ '& th': { fontWeight: 700 } }}>
                      <TableCell>Item</TableCell><TableCell align="center">Qty</TableCell><TableCell>Condition</TableCell><TableCell>Remarks</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {selected.items.map((it, i) => (
                        <TableRow key={i}>
                          <TableCell>{it.name || '—'}</TableCell>
                          <TableCell align="center">{it.quantity ?? '—'}</TableCell>
                          <TableCell>{it.condition || '—'}</TableCell>
                          <TableCell>{it.remarks || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {canEdit && <Button startIcon={<EditIcon />} onClick={() => { setOpenView(false); openEdit(selected); }}>Edit</Button>}
          <Button onClick={() => setOpenView(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Property</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete <strong>{selected?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PropertyManagement;
